<script setup>
import { defineAsyncComponent, reactive, computed, ref, onMounted, watchEffect } from "vue";

import { qModEvent } from "@/utils/eventemitter";
import { store } from "@/stores/global.js";

const props = defineProps({
    qmWMID: {
        type: Number,
        default: 101894,
    },
    qmDOMid: {
        type: String,
        default: "tool",
    },
    qmEnv: {
        type: String,
        default: "uat",
    },
    qmSID: {
        type: String,
        default: "",
    },
    qmComponentName: {
        type: String,
        required: true,
    },
    qmComponentProps: {
        type: Object,
        default: () => ({}),
    },
});

const mainComponent = ref(null);
const isFirstLoad = ref(true);

const state = reactive({
    compToLoad: "",
    breakPoint: "",
    componentProps: {},
});

state.compToLoad = props.qmComponentName;
state.componentProps = props.qmComponentProps;

const computedTool = computed(() =>
    defineAsyncComponent(
        () => import(`@/components/full/${state.compToLoad.toLowerCase()}/${state.compToLoad}.vue`)
    )
);

const filterObject = (obj, predicate) =>
    Object.keys(obj)
        .filter(key => predicate(obj[key]))
        .reduce((res, key) => ((res = key), res), {});

const breakpointsQMOD = {
    xs: 0,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    "2xl": 1536,
};

const useResizeObserver = element => {
    const ro = new ResizeObserver(entries => {
        for (let entry of entries) {
            const cr = entry.contentRect;
            state.breakPoint = filterObject(breakpointsQMOD, BP => BP < cr.width);
        }
    });
    ro.observe(element);
    return ro;
};

watchEffect(() => {
    if (mainComponent.value) {
        qModEvent({
            type: "TOOL",
            event: "LOADED",
            name: state.compToLoad,
        });

        const ro = useResizeObserver(mainComponent.value);

        if (isFirstLoad.value) {
            store.metrics.add(state.compToLoad, "loaded", state.componentProps, store.wmid);
            isFirstLoad.value = false;
        }
    } else {
        // not mounted yet, or the element was unmounted (e.g. by v-if)
    }
});
</script>

<template lang="pug">
Suspense
    div(ref="mainComponent", :class="['qmodii-main', `qmodii-breakpoint-${state.breakPoint}`]")
        Component(:is="computedTool", v-bind="props.qmComponentProps")
</template>

<style>
@import "./styles/css_vars.styl";

.qmodii-main {
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--qmodii-bg-color);
    box-sizing: border-box;

    .qmodii-ui-tool {
        height: 100%;
        gap: var(--qmodii-spacing-md);
    }
}

@container main (max-width: 568px) {
    .qmodii-main .qmodii-ui-tool {
        gap: var(--qmodii-spacing-sm);
    }
}
</style>
